import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSearchUsersQuery,
  useSendDownloadLinkMutation
} from '@ports/internal-services/user/userSlice';
import { SelectedUser, ShareModalProps } from '@business/interfaces/photo';
import { Button, Input } from '@business/workflows/ComponentUI';
import { Avatar } from '@business/workflows/ComponentUI/Avatar';

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  onShare,
  link
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // API mutation
  const [sendDownloadLink] = useSendDownloadLinkMutation();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search users API
  const { data: searchResults } = useSearchUsersQuery(
    { q: debouncedQuery, limit: 10 },
    { skip: !debouncedQuery || debouncedQuery.length < 2 }
  );

  const handleSelectUser = (user: any) => {
    const newUser: SelectedUser = {
      userId: user.userId,
      fullName: user.fullName,
      email: user.email,
      profileImage: user.profileImage
    };

    // Check if user is already selected
    if (!selectedUsers.find(u => u.userId === newUser.userId)) {
      setSelectedUsers([...selectedUsers, newUser]);
    }
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(u => u.userId !== userId));
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) return;

    setIsSharing(true);
    try {
      const userIds = selectedUsers.map(user => user.userId);
      const result = await sendDownloadLink({
        body: {
          userIds,
          link
        }
      }).unwrap();

      // Call parent callback if provided
      onShare?.(selectedUsers);

      // Show success message if needed
      if (result.data?.success && result.data.success.length > 0) {
        console.log('Successfully shared with:', result.data.success);
      }
      if (result.data?.failed && result.data.failed.length > 0) {
        console.warn('Failed to share with:', result.data.failed);
      }

      // Reset and close
      setSelectedUsers([]);
      setSearchQuery('');
      onClose();
    } catch (error) {
      console.error('Failed to share:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {t('mediaView.shareTitle')}
          </h2>
          <Button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            btnStyle="no-outline">
            ×
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('mediaView.shareMessage')}
          </p>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('mediaView.sharedWith')}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <div
                    key={user.userId}
                    className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                      <Avatar
                        alt={user?.fullName}
                        imgClassName="rounded-full w-12 h-12 border border-gray-300 mb-2"
                        src={user?.profileImage}
                        size="h-12 w-12"
                        isHeader={true}
                      />
                    </div>
                    <span className="text-sm text-gray-800">
                      {user.fullName}
                    </span>
                    <Button
                      onClick={() => handleRemoveUser(user.userId)}
                      btnStyle="no-outline"
                      className="text-gray-500 hover:text-gray-700 text-lg leading-none">
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('mediaView.shareWith')}
            </label>
            <Input
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={t('mediaView.searchUsers')}
              className="w-full px-4 py-2 border text-gray-500 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />

            {/* Dropdown Results */}
            {showDropdown && debouncedQuery.length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {searchResults?.data && searchResults.data.length > 0 ? (
                  searchResults.data.map((user: any) => (
                    <Button
                      key={user.userId}
                      onClick={() => handleSelectUser(user)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      disabled={selectedUsers.some(
                        u => u.userId === user.userId
                      )}
                      btnStyle="no-outline">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        <Avatar
                          alt={user?.fullName}
                          imgClassName="rounded-full w-8 h-8"
                          src={user?.profileImage}
                          size="h-8 w-8"
                          isHeader={true}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {user.fullName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user.email}
                        </p>
                      </div>
                      {selectedUsers.some(u => u.userId === user.userId) && (
                        <span className="text-xs text-green-600 font-medium">
                          {t('mediaView.selected')}
                        </span>
                      )}
                    </Button>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {t('mediaView.noUsersFound')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <Button
            onClick={handleClose}
            btnStyle="no-outline"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
            {t('cancel')}
          </Button>
          <Button
            btnStyle="no-outline"
            onClick={handleShare}
            disabled={selectedUsers.length === 0 || isSharing}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isSharing ? t('submitting') : t('mediaView.share')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
